// src/components/document/DocumentPolicyEditor.tsx
// Form for configuring document policy for a scope entity.
// Sections: Document types, Numbering, Approval, Acknowledgment, Versioning, Confidentiality, Registration, Inheritance.

import { useState, useCallback } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { NumberingPreview } from './NumberingPreview';
import type { PolicyFormData } from '@/features/document/types';
import {
  getDefaultPolicyFormData,
  validatePolicyForm,
  buildPolicyRequest,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Collapsible section
// ---------------------------------------------------------------------------

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <CardHeader>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center justify-between w-full"
        >
          <CardTitle>{title}</CardTitle>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </CardHeader>
      {open && <CardContent className="space-y-4">{children}</CardContent>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------

interface DocumentPolicyEditorProps {
  policy?: any;
  entityType: string;
  entityId: string;
  onSave: (data: any) => Promise<void>;
}

export const DocumentPolicyEditor = observer(({
  policy,
  entityType,
  entityId,
  onSave,
}: DocumentPolicyEditorProps) => {
  const { t } = useI18n();
  const [form, setForm] = useState<PolicyFormData>(() =>
    getDefaultPolicyFormData(policy),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | string | null>(null);

  const update = useCallback(<K extends keyof PolicyFormData>(key: K, value: PolicyFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const errors = validatePolicyForm(form);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await onSave(buildPolicyRequest(form, entityType, entityId));
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsSaving(false);
    }
  }, [form, entityType, entityId, onSave]);

  return (
    <div className="space-y-4">
      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      {/* Inheritance */}
      <Section title={t('documentPolicy.inheritance')}>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.inherit_from_parent}
            onChange={(e) => update('inherit_from_parent', e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-foreground">{t('documentPolicy.inheritFromParent')}</span>
        </label>
        {form.inherit_from_parent && (
          <p className="text-xs text-muted-foreground pl-7">
            {t('documentPolicy.inheritHint')}
          </p>
        )}
      </Section>

      {/* Document Types */}
      <Section title={t('documentPolicy.documentTypes')}>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('documentPolicy.allowedTypes')}
          </label>
          <textarea
            value={form.allowed_document_type_ids.join(', ')}
            onChange={(e) =>
              update(
                'allowed_document_type_ids',
                e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              )
            }
            placeholder={t('documentPolicy.allowedTypesPlaceholder')}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          />
          <p className="text-xs text-muted-foreground mt-1">{t('documentPolicy.allowedTypesHint')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('documentPolicy.defaultType')}
          </label>
          <input
            type="text"
            value={form.default_document_type_id}
            onChange={(e) => update('default_document_type_id', e.target.value)}
            placeholder={t('documentPolicy.defaultTypePlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </Section>

      {/* Numbering */}
      <Section title={t('documentPolicy.numbering')}>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('documentPolicy.numberingScheme')}
          </label>
          <input
            type="text"
            value={form.numbering_scheme_id}
            onChange={(e) => update('numbering_scheme_id', e.target.value)}
            placeholder={t('documentPolicy.numberingSchemePlaceholder')}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('documentPolicy.numberingPrefix')}
          </label>
          <input
            type="text"
            value={form.numbering_prefix}
            onChange={(e) => update('numbering_prefix', e.target.value)}
            placeholder="ORD"
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        {form.numbering_scheme_id && (
          <NumberingPreview
            schemeId={form.numbering_scheme_id}
            prefix={form.numbering_prefix}
          />
        )}
      </Section>

      {/* Approval */}
      <Section title={t('documentPolicy.approval')}>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.require_approval}
            onChange={(e) => update('require_approval', e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-foreground">{t('documentPolicy.requireApproval')}</span>
        </label>
        {form.require_approval && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('documentPolicy.approvalPipeline')}
            </label>
            <input
              type="text"
              value={form.default_approval_pipeline_id}
              onChange={(e) => update('default_approval_pipeline_id', e.target.value)}
              placeholder={t('documentPolicy.approvalPipelinePlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        )}
      </Section>

      {/* Acknowledgment */}
      <Section title={t('documentPolicy.acknowledgment')} defaultOpen={false}>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.require_acknowledgment}
            onChange={(e) => update('require_acknowledgment', e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-foreground">{t('documentPolicy.requireAcknowledgment')}</span>
        </label>
      </Section>

      {/* Versioning */}
      <Section title={t('documentPolicy.versioning')} defaultOpen={false}>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.enable_versioning}
            onChange={(e) => update('enable_versioning', e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-foreground">{t('documentPolicy.enableVersioning')}</span>
        </label>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.enable_review_cycle}
            onChange={(e) => update('enable_review_cycle', e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-foreground">{t('documentPolicy.enableReviewCycle')}</span>
        </label>
        {form.enable_review_cycle && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('documentPolicy.defaultReviewPeriod')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={form.default_review_period_days}
                onChange={(e) => update('default_review_period_days', parseInt(e.target.value) || 0)}
                min={0}
                className="w-24 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <span className="text-sm text-muted-foreground">{t('common.days')}</span>
            </div>
          </div>
        )}
      </Section>

      {/* Confidentiality */}
      <Section title={t('documentPolicy.confidentiality')} defaultOpen={false}>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('documentPolicy.defaultConfidentiality')}
          </label>
          <Select
            value={form.default_confidentiality}
            onChange={(e) => update('default_confidentiality', e.target.value)}>
            <option value="">{t('common.none')}</option>
            <option value="DOCUMENT_CONFIDENTIALITY_PUBLIC">{t('document.confidentiality.public')}</option>
            <option value="DOCUMENT_CONFIDENTIALITY_INTERNAL">{t('document.confidentiality.internal')}</option>
            <option value="DOCUMENT_CONFIDENTIALITY_RESTRICTED">{t('document.confidentiality.restricted')}</option>
            <option value="DOCUMENT_CONFIDENTIALITY_CONFIDENTIAL">{t('document.confidentiality.confidential')}</option>
            <option value="DOCUMENT_CONFIDENTIALITY_SECRET">{t('document.confidentiality.secret')}</option>
          </Select>
        </div>
      </Section>

      {/* Registration */}
      <Section title={t('documentPolicy.registration')} defaultOpen={false}>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.enable_registration}
            onChange={(e) => update('enable_registration', e.target.checked)}
            className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
          />
          <span className="text-sm text-foreground">{t('documentPolicy.enableRegistration')}</span>
        </label>
        {form.enable_registration && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {t('documentPolicy.registrationPrefix')}
            </label>
            <input
              type="text"
              value={form.registration_prefix}
              onChange={(e) => update('registration_prefix', e.target.value)}
              placeholder="REG"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        )}
      </Section>

      {/* Save */}
      <div className="flex items-center gap-3 pt-4">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
      </div>
    </div>
  );
});

export default DocumentPolicyEditor;
